import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { pmndpAPI } from '../../services/pmndp.api';

// Async thunks for API calls
export const fetchMachines = createAsyncThunk(
  'pmndp/fetchMachines',
  async (_, { rejectWithValue }) => {
    try {
      const response = await pmndpAPI.getMachineList();
      return response.data?.dataGet || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch machines');
    }
  }
);

export const fetchSlots = createAsyncThunk(
  'pmndp/fetchSlots',
  async (params, { rejectWithValue }) => {
    try {
      const response = await pmndpAPI.getSlotList(params);
      return response.data?.dataGet || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch slots');
    }
  }
);

export const fetchDialysisList = createAsyncThunk(
  'pmndp/fetchDialysisList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await pmndpAPI.getDialysisList(params);
      return response.data?.dataGet || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dialysis list');
    }
  }
);

export const createMachine = createAsyncThunk(
  'pmndp/createMachine',
  async (machineData, { rejectWithValue, dispatch }) => {
    try {
      const response = await pmndpAPI.createMachine(machineData);
      if (response.success) {
        dispatch(fetchMachines());
        return response;
      }
      return rejectWithValue(response.msg || 'Failed to create machine');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create machine');
    }
  }
);

export const createSlot = createAsyncThunk(
  'pmndp/createSlot',
  async (slotData, { rejectWithValue, dispatch }) => {
    try {
      const response = await pmndpAPI.createSlot(slotData);
      if (response.success) {
        dispatch(fetchSlots());
        return response;
      }
      return rejectWithValue(response.msg || 'Failed to create slot');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create slot');
    }
  }
);

const initialState = {
  // Data
  machines: [],
  slots: [],
  dialysisList: [],
  appointments: [],
  
  // UI State
  loading: {
    machines: false,
    slots: false,
    dialysisList: false,
    appointments: false,
  },
  
  // Error handling
  errors: {
    machines: null,
    slots: null,
    dialysisList: null,
    appointments: null,
  },
  
  // Form states
  selectedMachine: null,
  selectedSlot: null,
  selectedPatient: null,
  
  // Filters
  filters: {
    date: null,
    machineType: null,
    status: null,
  },
  
  // Cache
  lastFetch: {
    machines: null,
    slots: null,
    dialysisList: null,
  }
};

const pmndpSlice = createSlice({
  name: 'pmndp',
  initialState,
  reducers: {
    // UI actions
    setSelectedMachine: (state, action) => {
      state.selectedMachine = action.payload;
    },
    setSelectedSlot: (state, action) => {
      state.selectedSlot = action.payload;
    },
    setSelectedPatient: (state, action) => {
      state.selectedPatient = action.payload;
    },
    
    // Filter actions
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    
    // Error handling
    clearError: (state, action) => {
      const errorType = action.payload;
      if (errorType) {
        state.errors[errorType] = null;
      } else {
        state.errors = initialState.errors;
      }
    },
    
    // Cache management
    invalidateCache: (state, action) => {
      const cacheType = action.payload;
      if (cacheType) {
        state.lastFetch[cacheType] = null;
      } else {
        state.lastFetch = initialState.lastFetch;
      }
    },
  },
  extraReducers: (builder) => {
    // Machines
    builder
      .addCase(fetchMachines.pending, (state) => {
        state.loading.machines = true;
        state.errors.machines = null;
      })
      .addCase(fetchMachines.fulfilled, (state, action) => {
        state.loading.machines = false;
        state.machines = action.payload;
        state.lastFetch.machines = Date.now();
      })
      .addCase(fetchMachines.rejected, (state, action) => {
        state.loading.machines = false;
        state.errors.machines = action.payload;
      })
      
    // Slots
      .addCase(fetchSlots.pending, (state) => {
        state.loading.slots = true;
        state.errors.slots = null;
      })
      .addCase(fetchSlots.fulfilled, (state, action) => {
        state.loading.slots = false;
        state.slots = action.payload;
        state.lastFetch.slots = Date.now();
      })
      .addCase(fetchSlots.rejected, (state, action) => {
        state.loading.slots = false;
        state.errors.slots = action.payload;
      })
      
    // Dialysis List
      .addCase(fetchDialysisList.pending, (state) => {
        state.loading.dialysisList = true;
        state.errors.dialysisList = null;
      })
      .addCase(fetchDialysisList.fulfilled, (state, action) => {
        state.loading.dialysisList = false;
        state.dialysisList = action.payload;
        state.lastFetch.dialysisList = Date.now();
      })
      .addCase(fetchDialysisList.rejected, (state, action) => {
        state.loading.dialysisList = false;
        state.errors.dialysisList = action.payload;
      })
      
    // Create Machine
      .addCase(createMachine.pending, (state) => {
        state.loading.machines = true;
      })
      .addCase(createMachine.fulfilled, (state) => {
        state.loading.machines = false;
      })
      .addCase(createMachine.rejected, (state, action) => {
        state.loading.machines = false;
        state.errors.machines = action.payload;
      })
      
    // Create Slot
      .addCase(createSlot.pending, (state) => {
        state.loading.slots = true;
      })
      .addCase(createSlot.fulfilled, (state) => {
        state.loading.slots = false;
      })
      .addCase(createSlot.rejected, (state, action) => {
        state.loading.slots = false;
        state.errors.slots = action.payload;
      });
  },
});

export const {
  setSelectedMachine,
  setSelectedSlot,
  setSelectedPatient,
  setFilters,
  clearFilters,
  clearError,
  invalidateCache,
} = pmndpSlice.actions;

export default pmndpSlice.reducer;